import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import {
  PerformanceChartResolution,
  PerformanceReturnMethod,
} from "@repo/types";
import {
  BasicIndex,
  createCollection,
  eq,
  type InitialQueryBuilder,
  materialize,
  toArray,
} from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import { TRPCClientError } from "@trpc/client";
import {
  queryCollectionOptions,
  type QueryCollectionMeta,
  type SimpleComparison,
} from "@tanstack/query-db-collection";
import {
  DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  getSessionScopeTokenFromQueryKey,
  queryKeys,
  withSessionQueryScope,
} from "~/lib/query-keys";
import { queryClient } from "~/lib/query-client";
import { toScopedCollectionId } from "~/lib/db/collection-helpers";
import { type PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import { getBrowserPlatformApiUnbatchedClient } from "~/lib/trpc/browser-client";
import {
  coerceBoolean,
  coerceFiniteInteger,
  coerceFiniteNumber,
  type PortfolioDashboardAssetSummaryModel,
  type PortfolioAssetModel,
  type PortfolioCashSummaryModel,
  type PortfolioAssetUniverseModel,
  type PortfolioAssetPriceModel,
  type PortfolioPositionModel,
  type PortfolioInstanceModel,
  type PortfolioVaultModel,
  normalizePortfolioAddress,
  type PortfolioAssetPnlModel,
  type PortfolioAllocationHistoryCheckpointModel,
  type PortfolioHistoryEventModel,
  type PortfolioPerformancePointModel,
  type PortfolioPerformanceSeriesPointModel,
  type PortfolioPerformanceSummaryModel,
  type PortfolioPerformanceWindowReturn,
  type PortfolioPerformanceWindowStatsModel,
  type PortfolioScheduleModel,
  type PortfolioScheduledFunctionModel,
  type PortfolioWorkflowModel,
} from "./models";
import {
  applyOwnershipFallback,
  asRecord,
  type DashboardNormalizedResponse,
  mapAllocationHistoryCheckpointModels,
  mapPerformanceLiveAssetCollectionModel,
  mapPerformancePointModels,
  mapPerformanceSeriesModels,
  normalizePortfolioInstanceModel,
  type PerformanceLiveAssetCollectionModels,
  PERFORMANCE_LIVE_POSITION_SOURCE,
  type PortfolioAllocationHistoryChartResponse,
  type PortfolioDetailNormalizedResponse,
  type PortfolioPerformanceSeriesPayload,
  type PortfolioPerformanceSeriesResponse,
  readNonEmptyString,
  readString,
  readTimestampMs,
} from "./normalizers";
import {
  extractEqFilterValue,
  extractInFilterValues,
  parseSubsetFilters,
} from "./query-filters";

export const STRATEGY_BATCH_LIMIT = 50;
export const ASSET_BATCH_LIMIT = 50;
const DETAIL_BATCH_LIMIT = 20;
const MAX_COLLECTION_CACHE_SIZE = 10;
export const PORTFOLIO_DETAIL_BOOTSTRAP_STALE_TIME_MS = 30_000;
export const DASHBOARD_SEED_STALE_WHILE_REVALIDATE_TTL_MS = 30_000;
export const DASHBOARD_SEED_OWNER_SCOPE_CACHE_ID = "__owner_scope__";
export const DASHBOARD_SEED_INCLUDE_CACHE_KEY = {
  assetPrices: true,
  assets: true,
  dashboardAssetSummaries: true,
  performance: false,
  portfolioInstances: true,
  positions: true,
  vaults: true,
} as const;

export interface PortfolioDbDependencies {
  ownerAddresses?: string[];
  platformApi: PlatformApiTrpcClient;
  queryClient: QueryClient;
  sessionScopeToken?: string;
}

type DashboardNormalizedInput = Exclude<
  Parameters<
    PlatformApiTrpcClient["strategyInstances"]["getDashboardNormalizedData"]["query"]
  >[0],
  undefined
>;

export type PortfolioDetailNormalizedInput = Exclude<
  Parameters<
    PlatformApiTrpcClient["strategyInstances"]["getPortfolioDetailNormalizedData"]["query"]
  >[0],
  undefined
>;

type PortfolioWorkflowsListResponse = Awaited<
  ReturnType<PlatformApiTrpcClient["v2"]["workflows"]["list"]["query"]>
>;
type PortfolioScheduledFunctionsListResponse = Awaited<
  ReturnType<
    PlatformApiTrpcClient["v2"]["portfolio"]["scheduledFunctions"]["list"]["query"]
  >
>;
type PortfolioWorkflowsListInput = Parameters<
  PlatformApiTrpcClient["v2"]["workflows"]["list"]["query"]
>[0];
type PortfolioWorkflowListKind = NonNullable<
  PortfolioWorkflowsListInput["kind"]
>;
type PortfolioWorkflowListStatus = NonNullable<
  PortfolioWorkflowsListInput["status"]
>;

const PORTFOLIO_WORKFLOW_LIST_KINDS = [
  "rebalance_portfolio",
  "buy_asset",
  "sell_asset",
  "cash_sweep",
  "recurring_deposit",
  "withdraw_funds",
  "bridge_and_invest",
  "permission_refresh",
  "monitor_then_action",
] as const satisfies readonly PortfolioWorkflowListKind[];

const PORTFOLIO_WORKFLOW_LIST_STATUSES = [
  "draft",
  "active",
  "paused",
  "pending_approval",
  "running",
  "completed",
  "failed",
  "archived",
] as const satisfies readonly PortfolioWorkflowListStatus[];

export function emptyPortfolioPerformanceSeriesResponse(
  portfolioId: string,
): PortfolioPerformanceSeriesResponse {
  return {
    live: {
      assets: [],
      value: "0",
    },
    meta: {
      currency: "USD",
      returnMethod: PerformanceReturnMethod.MWR,
    },
    portfolioId,
    series: [],
    stats: {},
  };
}

function isPortfolioPerformanceSeriesNotFound(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    (error.data?.httpStatus === 404 || error.data?.code === "NOT_FOUND")
  );
}

export interface DashboardNormalizedPayload {
  assetPrices?: DashboardNormalizedResponse["assetPrices"];
  assets?: DashboardNormalizedResponse["assets"];
  cashSummaries?: DashboardNormalizedResponse["cashSummaries"];
  dashboardAssetSummaries?: DashboardNormalizedResponse["dashboardAssetSummaries"];
  performanceSeries?: DashboardNormalizedResponse["performanceSeries"];
  positions?: DashboardNormalizedResponse["positions"];
  portfolioInstances?: DashboardNormalizedResponse["portfolioInstances"];
  vaults?: DashboardNormalizedResponse["vaults"];
}

export interface PortfolioSummaryHydrationInclude {
  assetPrices: boolean;
  assets: boolean;
  dashboardAssetSummaries: boolean;
  performance: boolean;
  performanceMaxPointsPerPortfolio?: number;
  performanceResolution?: PerformanceChartResolution;
  portfolioInstances: boolean;
  positions: boolean;
  vaults: boolean;
}

export interface PortfolioDetailViewTokenRow {
  asset?: PortfolioAssetModel;
  assetPrice?: PortfolioAssetPriceModel;
  position: PortfolioPositionModel;
}

export interface PortfolioDetailViewRow {
  assetPnl: PortfolioAssetPnlModel[];
  historyEvents: PortfolioHistoryEventModel[];
  liveTokenRows: PortfolioDetailViewTokenRow[];
  performancePoints: PortfolioPerformancePointModel[];
  performanceSeries: PortfolioPerformanceSeriesPointModel[];
  performanceSummary?: PortfolioPerformanceSummaryModel;
  performanceWindowStats?: PortfolioPerformanceWindowStatsModel;
  portfolioAssetRows: PortfolioDetailViewAssetRow[];
  portfolioInstance: PortfolioInstanceModel;
  portfolioSchedule?: PortfolioScheduleModel;
  tokenRows: PortfolioDetailViewTokenRow[];
  vaults: PortfolioVaultModel[];
}

export interface PortfolioDetailViewAssetRow {
  asset?: PortfolioAssetModel;
  assetPrice?: PortfolioAssetPriceModel;
  link: PortfolioAssetUniverseModel;
}

export interface PortfolioDetailBootstrapInput {
  clearPerformanceLivePositionsOnError?: boolean;
  historyLimit?: number;
  ownerAddresses?: string[];
  performanceResolution?: PortfolioDetailNormalizedInput["performanceResolution"];
  platformApi?: PlatformApiTrpcClient;
  portfolioId: string;
  positionsInSharesUiEnabled?: boolean;
  queryClient?: QueryClient;
  sessionScopeToken?: string;
}

export const PORTFOLIO_SUMMARY_COLLECTION_QUERY_KEYS = [
  queryKeys.portfolio.portfolioInstances,
  queryKeys.portfolio.positions,
  queryKeys.portfolio.livePositions,
  queryKeys.portfolio.assetUniverse,
  queryKeys.portfolio.vaults,
  queryKeys.portfolio.assets,
  queryKeys.portfolio.assetPrices,
  queryKeys.portfolio.dashboardAssetSummaries,
] as const;

export const PORTFOLIO_DETAIL_PORTFOLIO_BOUND_QUERY_KEYS = [
  queryKeys.portfolio.allocationHistoryCheckpoints,
  queryKeys.portfolio.dashboardAssetSummaries,
  queryKeys.portfolio.portfolioInstances,
  queryKeys.portfolio.positions,
  queryKeys.portfolio.vaults,
  queryKeys.portfolio.assetUniverse,
  queryKeys.portfolio.schedules,
  queryKeys.portfolio.performancePoints,
  queryKeys.portfolio.performanceSeries,
  queryKeys.portfolio.performanceSummaries,
  queryKeys.portfolio.historyEvents,
  queryKeys.portfolio.assetPnl,
  queryKeys.portfolio.livePositions,
  queryKeys.portfolio.workflows,
  queryKeys.portfolio.scheduledFunctions,
] as const;

export function chunkArray<T>(values: T[], chunkSize: number): T[][] {
  if (values.length === 0) {
    return [];
  }

  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }

  return chunks;
}

export function normalizePositiveInteger(
  value: number | undefined,
  fallback: number,
): number {
  if (!Number.isFinite(value) || value === undefined) {
    return fallback;
  }

  const normalized = Math.floor(value);
  return normalized > 0 ? normalized : fallback;
}

export function uniqueNonEmpty(values: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const output: string[] = [];

  for (const value of values) {
    if (!value) {
      continue;
    }

    const trimmed = value.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    output.push(trimmed);
  }

  return output;
}

export function uniqueNormalizedAddresses(
  values: Array<string | undefined>,
): string[] {
  const seen = new Set<string>();
  const normalizedAddresses: string[] = [];

  for (const value of values) {
    if (!value) {
      continue;
    }

    const normalizedAddress = normalizePortfolioAddress(value);
    if (normalizedAddress.length === 0 || seen.has(normalizedAddress)) {
      continue;
    }

    seen.add(normalizedAddress);
    normalizedAddresses.push(normalizedAddress);
  }

  return normalizedAddresses;
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const deduped = new Map<string, T>();

  for (const item of items) {
    deduped.set(item.id, item);
  }

  return Array.from(deduped.values());
}

function dedupeByKey<T>(items: T[], getKey: (item: T) => string): T[] {
  const deduped = new Map<string, T>();

  for (const item of items) {
    deduped.set(getKey(item), item);
  }

  return Array.from(deduped.values());
}

interface CollectionRowsReader<T> {
  values: () => IterableIterator<T>;
}

interface CollectionRowsUpserter<
  T extends object,
> extends CollectionRowsReader<T> {
  utils: {
    writeUpsert: (data: T | T[]) => void;
  };
}

export function readCollectionRows<T>(
  collection: CollectionRowsReader<T> | undefined,
): T[] {
  return collection ? Array.from(collection.values()) : [];
}

export function canReadCollectionRows<T>(
  collection: unknown,
): collection is CollectionRowsReader<T> {
  if (!collection || typeof collection !== "object") {
    return false;
  }

  return typeof (collection as { values?: unknown }).values === "function";
}

// Query collections treat each queryFn result as the complete synced state.
// For on-demand subset fetches, replace only the targeted slice and preserve
// previously loaded rows outside that slice.
function replaceSubsetRows<T extends { id: string }>(input: {
  collection: CollectionRowsReader<T> | undefined;
  fetchedRows: T[];
  matchesSubset: (row: T) => boolean;
}): T[] {
  const merged = new Map<string, T>();

  for (const row of readCollectionRows(input.collection)) {
    if (input.matchesSubset(row)) {
      continue;
    }

    merged.set(row.id, row);
  }

  for (const row of input.fetchedRows) {
    merged.set(row.id, row);
  }

  return Array.from(merged.values());
}

const OWNER_SCOPE_QUERY_KEY_SEGMENT = "owner-scope" as const;

function withPortfolioCollectionQueryScope(
  baseKey: ReadonlyArray<unknown>,
  input: {
    ownerAddresses?: string[];
    sessionScopeToken?: string;
  },
): ReadonlyArray<unknown> {
  const sessionScopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const ownerScopeToken = toOwnersCacheKey(
    uniqueNormalizedAddresses(input.ownerAddresses ?? []),
  );

  return [
    ...withSessionQueryScope(baseKey, {
      scopeToken: sessionScopeToken,
    }),
    OWNER_SCOPE_QUERY_KEY_SEGMENT,
    ownerScopeToken,
  ];
}

function getQueryCollectionMeta(query: {
  options: { meta?: unknown };
}): QueryCollectionMeta | undefined {
  return query.options.meta as QueryCollectionMeta | undefined;
}

export function matchesQueryKeyPrefix(
  queryKey: readonly unknown[],
  prefix: readonly unknown[],
): boolean {
  if (queryKey.length < prefix.length) {
    return false;
  }

  for (let index = 0; index < prefix.length; index += 1) {
    if (queryKey[index] !== prefix[index]) {
      return false;
    }
  }

  return true;
}

export async function invalidatePortfolioDomainCollectionQueries(
  queryKeysToInvalidate: ReadonlyArray<ReadonlyArray<unknown>>,
  sessionScopeToken?: string,
): Promise<void> {
  const sessionScope = {
    scopeToken: sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  };

  await Promise.all(
    queryKeysToInvalidate.map(async (queryKey) => {
      await queryClient.invalidateQueries({
        queryKey: withSessionQueryScope(queryKey, sessionScope),
      });
    }),
  );
}

export function invalidatePortfolioDomainDetailQueries(input: {
  portfolioId: string;
  queryKeysToInvalidate: ReadonlyArray<ReadonlyArray<unknown>>;
  sessionScopeToken?: string;
}): Promise<void> {
  const sessionScopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;

  return queryClient.invalidateQueries({
    predicate: (query) => {
      if (
        getSessionScopeTokenFromQueryKey(query.queryKey) !== sessionScopeToken
      ) {
        return false;
      }

      if (
        !input.queryKeysToInvalidate.some((queryKey) =>
          matchesQueryKeyPrefix(query.queryKey, queryKey),
        )
      ) {
        return false;
      }

      const { filters } = parseSubsetFilters(getQueryCollectionMeta(query));
      return extractPortfolioIds(filters).includes(input.portfolioId);
    },
  });
}

function extractPortfolioIds(filters: SimpleComparison[]): string[] {
  const eqId = extractEqFilterValue<string>(filters, "id");
  const eqPortfolioId = extractEqFilterValue<string>(filters, "portfolioId");
  const inId = extractInFilterValues<string>(filters, "id");
  const inPortfolioId = extractInFilterValues<string>(filters, "portfolioId");

  return uniqueNonEmpty([eqId, eqPortfolioId, ...inId, ...inPortfolioId]);
}

function extractAssetIds(filters: SimpleComparison[]): string[] {
  const eqId = extractEqFilterValue<string>(filters, "id");
  const eqAssetId = extractEqFilterValue<string>(filters, "assetId");
  const inId = extractInFilterValues<string>(filters, "id");
  const inAssetId = extractInFilterValues<string>(filters, "assetId");
  const assetIds: string[] = [];
  for (const value of uniqueNonEmpty([
    eqId,
    eqAssetId,
    ...inId,
    ...inAssetId,
  ])) {
    const legacyAssetId = tryAssetIdLikeToLegacyAssetId(value);
    if (legacyAssetId) {
      assetIds.push(legacyAssetId);
    }
  }

  return assetIds;
}

function extractVaultIds(filters: SimpleComparison[]): string[] {
  const eqId = extractEqFilterValue<string>(filters, "id");
  const eqAccountId = extractEqFilterValue<string>(filters, "accountId");
  const inId = extractInFilterValues<string>(filters, "id");
  const inAccountId = extractInFilterValues<string>(filters, "accountId");

  return uniqueNonEmpty([eqId, eqAccountId, ...inId, ...inAccountId]);
}

function extractOwnerAddresses(filters: SimpleComparison[]): string[] {
  const eqOwnerAddress = extractEqFilterValue<string>(filters, "ownerAddress");
  const inOwnerAddress = extractInFilterValues<string>(filters, "ownerAddress");

  return uniqueNormalizedAddresses([eqOwnerAddress, ...inOwnerAddress]);
}

export function mergeNormalizedPayloads(
  payloads: DashboardNormalizedPayload[],
): DashboardNormalizedPayload {
  return {
    assetPrices: dedupeById(
      payloads.flatMap((payload) => payload.assetPrices ?? []),
    ),
    assets: dedupeById(payloads.flatMap((payload) => payload.assets ?? [])),
    dashboardAssetSummaries: dedupeById(
      payloads.flatMap((payload) => payload.dashboardAssetSummaries ?? []),
    ),
    cashSummaries: dedupeById(
      payloads.flatMap((payload) => payload.cashSummaries ?? []),
    ),
    performanceSeries: dedupeByKey(
      payloads.flatMap((payload) => payload.performanceSeries ?? []),
      (payload) => payload.portfolioId,
    ),
    positions: dedupeById(
      payloads.flatMap((payload) => payload.positions ?? []),
    ),
    portfolioInstances: dedupeById(
      payloads.flatMap((payload) => payload.portfolioInstances ?? []),
    ),
    vaults: dedupeById(payloads.flatMap((payload) => payload.vaults ?? [])),
  };
}

export function extractPortfolioIdsFromPayload(
  payload: DashboardNormalizedPayload,
): string[] {
  return uniqueNonEmpty(
    (payload.portfolioInstances ?? []).map((portfolio) => portfolio.id),
  );
}

function isMissingDashboardNormalizedProcedureError(error: Error): boolean {
  return (
    error.message.includes("strategyInstances.getDashboardNormalizedData") &&
    error.message.includes("No procedure found")
  );
}

function isMissingPortfolioDetailNormalizedProcedureError(
  error: Error,
): boolean {
  return (
    error.message.includes(
      "strategyInstances.getPortfolioDetailNormalizedData",
    ) && error.message.includes("No procedure found")
  );
}

export function normalizePortfolioNormalizedContractError(error: Error): Error {
  if (isMissingDashboardNormalizedProcedureError(error)) {
    return new Error(
      "[portfolio-db] Required backend contract `strategyInstances.getDashboardNormalizedData` is missing.",
    );
  }

  if (isMissingPortfolioDetailNormalizedProcedureError(error)) {
    return new Error(
      "[portfolio-db] Required backend contract `strategyInstances.getPortfolioDetailNormalizedData` is missing.",
    );
  }

  return error;
}

export async function queryDashboardNormalizedData(
  dependencies: PortfolioDbDependencies,
  input: DashboardNormalizedInput,
): Promise<DashboardNormalizedPayload> {
  // The API intentionally excludes Investing Account portfolios unless this
  // flag is true. Portfolio-domain callers treat a portfolioInstances response
  // as an authoritative graph-root projection, so centralize the invariant
  // here instead of relying on every hydration/query path to remember it.
  const normalizedInput: DashboardNormalizedInput =
    input.include?.portfolioInstances === true
      ? { ...input, includeInvestingAccount: true }
      : input;

  try {
    return await dependencies.platformApi.strategyInstances.getDashboardNormalizedData.query(
      normalizedInput,
    );
  } catch (error) {
    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

export async function queryPortfolioDetailNormalizedData(
  dependencies: PortfolioDbDependencies,
  input: PortfolioDetailNormalizedInput,
): Promise<PortfolioDetailNormalizedResponse> {
  try {
    return await dependencies.platformApi.strategyInstances.getPortfolioDetailNormalizedData.query(
      input,
    );
  } catch (error) {
    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

export async function queryPortfolioPerformanceSeriesData(
  dependencies: PortfolioDbDependencies,
  portfolioId: string,
): Promise<PortfolioPerformanceSeriesResponse> {
  try {
    return await dependencies.platformApi.v2.portfolio.performance.series.query(
      {
        portfolioId,
        returnMethod: PerformanceReturnMethod.MWR,
      },
    );
  } catch (error) {
    if (isPortfolioPerformanceSeriesNotFound(error)) {
      return emptyPortfolioPerformanceSeriesResponse(portfolioId);
    }

    if (
      error instanceof TRPCClientError &&
      (error.data?.httpStatus === 401 ||
        error.data?.httpStatus === 403 ||
        error.data?.code === "FORBIDDEN" ||
        error.data?.code === "UNAUTHORIZED")
    ) {
      try {
        return await dependencies.platformApi.v2.public.portfolio.performance.series.query(
          {
            portfolioId,
            returnMethod: PerformanceReturnMethod.MWR,
          },
        );
      } catch (publicError) {
        if (publicError instanceof Error) {
          throw normalizePortfolioNormalizedContractError(publicError);
        }

        throw publicError;
      }
    }

    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

async function queryPortfolioAllocationHistoryChartData(
  dependencies: PortfolioDbDependencies,
  portfolioId: string,
): Promise<PortfolioAllocationHistoryChartResponse> {
  try {
    return await dependencies.platformApi.v2.public.portfolio.activity.allocationHistoryChart.query(
      {
        portfolioId,
      },
    );
  } catch (error) {
    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

async function queryPortfolioWorkflowsData(
  dependencies: PortfolioDbDependencies,
  input: {
    kind?: PortfolioWorkflowListKind;
    limit: number;
    portfolioId: string;
    status?: PortfolioWorkflowListStatus;
  },
): Promise<PortfolioWorkflowsListResponse> {
  try {
    const request: PortfolioWorkflowsListInput = {
      ...(input.kind ? { kind: input.kind } : {}),
      limit: input.limit,
      portfolioId: input.portfolioId,
      ...(input.status ? { status: input.status } : {}),
    };

    return await dependencies.platformApi.v2.workflows.list.query(request);
  } catch (error) {
    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

export async function queryPortfolioScheduledFunctionsData(
  dependencies: PortfolioDbDependencies,
  portfolioId: string,
): Promise<PortfolioScheduledFunctionsListResponse> {
  try {
    return await dependencies.platformApi.v2.portfolio.scheduledFunctions.list.query(
      {
        includeTargeting: true,
        portfolioId,
      },
    );
  } catch (error) {
    if (error instanceof Error) {
      throw normalizePortfolioNormalizedContractError(error);
    }

    throw error;
  }
}

function readPortfolioWorkflowListKind(
  value: unknown,
): PortfolioWorkflowListKind | undefined {
  const stringValue = readNonEmptyString(value);
  if (
    !stringValue ||
    !(PORTFOLIO_WORKFLOW_LIST_KINDS as readonly string[]).includes(stringValue)
  ) {
    return undefined;
  }

  return stringValue as PortfolioWorkflowListKind;
}

function readPortfolioWorkflowListStatus(
  value: unknown,
): PortfolioWorkflowListStatus | undefined {
  const stringValue = readNonEmptyString(value);
  if (
    !stringValue ||
    !(PORTFOLIO_WORKFLOW_LIST_STATUSES as readonly string[]).includes(
      stringValue,
    )
  ) {
    return undefined;
  }

  return stringValue as PortfolioWorkflowListStatus;
}

function mapScheduleModel(
  portfolioId: string,
  rawSchedule: PortfolioDetailNormalizedResponse["schedule"],
): PortfolioScheduleModel | null {
  const scheduleRecord = asRecord(rawSchedule);
  if (!scheduleRecord) {
    return null;
  }

  const scheduleId = readString(scheduleRecord.scheduleId);
  const scheduleData = asRecord(scheduleRecord.scheduleData);
  const scheduleDescription =
    asRecord(scheduleData?.scheduleDescription) ??
    asRecord(scheduleRecord.scheduleDescription);
  const scheduleInfo = asRecord(scheduleDescription?.info);
  const nextActionTimes = Array.isArray(scheduleInfo?.nextActionTimes)
    ? scheduleInfo.nextActionTimes
    : [];

  const nextRunAt = nextActionTimes
    .map((nextActionTime) => readTimestampMs(nextActionTime))
    .find((timestamp): timestamp is number => timestamp !== undefined);

  const paused = coerceBoolean(scheduleData?.paused, false);
  const scheduleExists = coerceBoolean(scheduleRecord.scheduleExists, false);

  return {
    id: `${portfolioId}:${scheduleId ?? "schedule"}`,
    isActive: scheduleExists && !paused,
    nextRunAt,
    portfolioId,
    raw: rawSchedule,
    scheduleId,
    updatedAt: Date.now(),
  };
}

function readWorkflowPortfolioId(
  workflow: Record<string, unknown>,
  fallbackPortfolioId: string,
): string {
  const linkedPortfolioIds: string[] = [];
  if (Array.isArray(workflow.linkedPortfolioIds)) {
    for (const value of workflow.linkedPortfolioIds) {
      const portfolioId = readNonEmptyString(value);
      if (portfolioId) {
        linkedPortfolioIds.push(portfolioId);
      }
    }
  }
  if (linkedPortfolioIds.includes(fallbackPortfolioId)) {
    return fallbackPortfolioId;
  }

  const intent = asRecord(workflow.intent);
  const target = asRecord(workflow.target);
  const actionSpec = asRecord(workflow.actionSpec);

  return (
    readNonEmptyString(intent?.portfolioId) ??
    readNonEmptyString(target?.portfolioId) ??
    readNonEmptyString(actionSpec?.targetPortfolioId) ??
    linkedPortfolioIds[0] ??
    fallbackPortfolioId
  );
}

function mapPortfolioWorkflowModel(
  portfolioId: string,
  rawWorkflow: unknown,
): PortfolioWorkflowModel | null {
  const workflow = asRecord(rawWorkflow);
  const workflowId = readNonEmptyString(workflow?.workflowId);
  const kind = readNonEmptyString(workflow?.kind);
  const status = readNonEmptyString(workflow?.status);

  if (!workflow || !workflowId || !kind || !status) {
    return null;
  }

  const trigger = asRecord(workflow.trigger);
  const updatedAt =
    readTimestampMs(workflow.updatedAt) ??
    readTimestampMs(workflow.createdAt) ??
    Date.now();

  return {
    description: readString(workflow.description) ?? null,
    id: `${portfolioId}:${workflowId}`,
    kind,
    lastRunAt: readTimestampMs(workflow.lastRunAt),
    nextRunAt: readTimestampMs(workflow.nextRunAt),
    portfolioId: readWorkflowPortfolioId(workflow, portfolioId),
    raw: rawWorkflow,
    status,
    title: readNonEmptyString(workflow.title) ?? "Workflow",
    triggerKind: readNonEmptyString(trigger?.kind),
    updatedAt,
    workflowId,
  };
}

function mapPortfolioWorkflowModels(
  portfolioId: string,
  payload: PortfolioWorkflowsListResponse | null,
): PortfolioWorkflowModel[] {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const models: PortfolioWorkflowModel[] = [];
  for (const item of items) {
    const model = mapPortfolioWorkflowModel(portfolioId, item);
    if (model) {
      models.push(model);
    }
  }

  return models;
}

function readScheduledFunctionPortfolioId(
  scheduledFunction: Record<string, unknown>,
  fallbackPortfolioId: string,
): string {
  const config = asRecord(scheduledFunction.config);
  const functionKey = readNonEmptyString(scheduledFunction.functionKey);
  const targetStrategyInstanceId = readNonEmptyString(
    config?.targetStrategyInstanceId,
  );
  const recipientStrategyInstanceId = readNonEmptyString(
    config?.recipientStrategyInstanceId,
  );
  const sourceStrategyInstanceId = readNonEmptyString(
    scheduledFunction.strategyInstanceId,
  );

  if (functionKey === "recurring_account_deposit" && targetStrategyInstanceId) {
    return targetStrategyInstanceId;
  }

  return (
    targetStrategyInstanceId ??
    recipientStrategyInstanceId ??
    sourceStrategyInstanceId ??
    fallbackPortfolioId
  );
}

function mapPortfolioScheduledFunctionModel(
  portfolioId: string,
  rawScheduledFunction: unknown,
): PortfolioScheduledFunctionModel | null {
  const scheduledFunction = asRecord(rawScheduledFunction);
  if (!scheduledFunction) {
    return null;
  }

  const scheduleId = coerceFiniteInteger(scheduledFunction.id, -1);
  const functionKey = readNonEmptyString(scheduledFunction.functionKey);
  const sourcePortfolioId = readNonEmptyString(
    scheduledFunction.strategyInstanceId,
  );
  const intervalMs = coerceFiniteInteger(scheduledFunction.intervalMs, 0);
  const configFingerprint = readNonEmptyString(
    scheduledFunction.configFingerprint,
  );

  if (
    scheduleId <= 0 ||
    !functionKey ||
    !sourcePortfolioId ||
    intervalMs < 0 ||
    !configFingerprint
  ) {
    return null;
  }

  const effectivePortfolioId = readScheduledFunctionPortfolioId(
    scheduledFunction,
    portfolioId,
  );
  const startAt = readTimestampMs(scheduledFunction.startAt);
  const endAt = readTimestampMs(scheduledFunction.endAt);
  const lastRunAt = readTimestampMs(scheduledFunction.lastRunAt);

  return {
    config: scheduledFunction.config,
    configFingerprint,
    ...(endAt !== undefined ? { endAt } : {}),
    functionKey,
    id: `${effectivePortfolioId}:${scheduleId}`,
    intervalMs,
    isDeleted: coerceBoolean(scheduledFunction.isDeleted, false),
    ...(lastRunAt !== undefined ? { lastRunAt } : {}),
    portfolioId: effectivePortfolioId,
    raw: rawScheduledFunction,
    scheduleId,
    sourcePortfolioId,
    ...(startAt !== undefined ? { startAt } : {}),
    updatedAt: Date.now(),
  };
}

function mapPortfolioScheduledFunctionModels(
  portfolioId: string,
  payload: PortfolioScheduledFunctionsListResponse | null,
): PortfolioScheduledFunctionModel[] {
  const items = Array.isArray(payload?.scheduledFunctions)
    ? payload.scheduledFunctions
    : [];
  const models: PortfolioScheduledFunctionModel[] = [];
  for (const item of items) {
    const model = mapPortfolioScheduledFunctionModel(portfolioId, item);
    if (model) {
      models.push(model);
    }
  }

  return models;
}

function mapPerformanceSummaryModel(
  portfolioId: string,
  rawSummary: PortfolioDetailNormalizedResponse["performanceSummary"],
  rawSavings: PortfolioDetailNormalizedResponse["portfolioSavings"],
): PortfolioPerformanceSummaryModel | null {
  const summaryRecord = asRecord(rawSummary);
  if (!summaryRecord) {
    return null;
  }

  const stats = asRecord(summaryRecord.stats);
  const metrics = asRecord(stats?.metrics);
  const dataStatus = asRecord(summaryRecord.dataStatus);
  const savings = asRecord(rawSavings);

  return {
    currentValueUsd: coerceFiniteNumber(
      (typeof stats?.currentValue === "number" ||
      typeof stats?.currentValue === "string"
        ? stats.currentValue
        : undefined) ?? undefined,
    ),
    endValueUsd: coerceFiniteNumber(
      (typeof metrics?.endValueUsd === "number" ||
      typeof metrics?.endValueUsd === "string"
        ? metrics.endValueUsd
        : undefined) ?? undefined,
    ),
    feesSavedUsd: coerceFiniteNumber(
      (typeof savings?.estimatedFeesSavedUsd === "number" ||
      typeof savings?.estimatedFeesSavedUsd === "string"
        ? savings.estimatedFeesSavedUsd
        : undefined) ?? undefined,
    ),
    flowAdjustedReturnPercent: coerceFiniteNumber(
      (typeof metrics?.flowAdjustedReturn === "number" ||
      typeof metrics?.flowAdjustedReturn === "string"
        ? metrics.flowAdjustedReturn
        : undefined) ?? undefined,
    ),
    id: portfolioId,
    isNewPortfolio: coerceBoolean(dataStatus?.isNewPortfolio, false),
    liveDataError:
      typeof dataStatus?.liveDataError === "string"
        ? dataStatus.liveDataError
        : null,
    netDepositsUsd: coerceFiniteNumber(
      (typeof metrics?.netDepositAmountUpUntilEndTime === "number" ||
      typeof metrics?.netDepositAmountUpUntilEndTime === "string"
        ? metrics.netDepositAmountUpUntilEndTime
        : typeof metrics?.netFlowsUsd === "number" ||
            typeof metrics?.netFlowsUsd === "string"
          ? metrics.netFlowsUsd
          : undefined) ?? undefined,
    ),
    percentageChange: coerceFiniteNumber(
      (typeof metrics?.percentageChange === "number" ||
      typeof metrics?.percentageChange === "string"
        ? metrics.percentageChange
        : undefined) ?? undefined,
    ),
    portfolioId,
    startValueUsd: coerceFiniteNumber(
      (typeof metrics?.startValueUsd === "number" ||
      typeof metrics?.startValueUsd === "string"
        ? metrics.startValueUsd
        : undefined) ?? undefined,
    ),
    updatedAt: Date.now(),
  };
}

function mapPerformanceLiveAssetCollectionModels(
  portfolioId: string,
  payload: PortfolioPerformanceSeriesPayload | null,
): {
  assetPrices: PortfolioAssetPriceModel[];
  assets: PortfolioAssetModel[];
  positions: PortfolioPositionModel[];
} {
  const payloadRecord = asRecord(payload);
  const liveRecord = asRecord(payloadRecord?.live);
  const rawAssets = liveRecord?.assets;
  if (!Array.isArray(rawAssets)) {
    return {
      assetPrices: [],
      assets: [],
      positions: [],
    };
  }

  const updatedAt = Date.now();
  const mappedAssets = rawAssets
    .map((rawAsset) =>
      mapPerformanceLiveAssetCollectionModel(portfolioId, rawAsset, updatedAt),
    )
    .filter((asset): asset is PerformanceLiveAssetCollectionModels =>
      Boolean(asset),
    );

  return {
    assetPrices: dedupeById(mappedAssets.map((asset) => asset.assetPrice)),
    assets: dedupeById(mappedAssets.map((asset) => asset.asset)),
    positions: dedupeById(mappedAssets.map((asset) => asset.position)),
  };
}

function mapPortfolioAssetUniverseModels(
  portfolioId: string,
  assets: readonly PortfolioAssetModel[],
): PortfolioAssetUniverseModel[] {
  const updatedAt = Date.now();
  return assets.map((asset) => ({
    assetId: asset.id,
    id: `${portfolioId}:${asset.id}`,
    portfolioId,
    updatedAt,
  }));
}

function hasPerformanceLiveAssetsSnapshot(
  payload: PortfolioPerformanceSeriesPayload | null,
): boolean {
  const payloadRecord = asRecord(payload);
  const liveRecord = asRecord(payloadRecord?.live);
  return Array.isArray(liveRecord?.assets);
}

function readPortfolioPositionSource(position: unknown): string | undefined {
  const positionRecord = asRecord(position);
  return typeof positionRecord?.source === "string"
    ? positionRecord.source
    : undefined;
}

// Indexed position queries own indexed rows, while performance hydration owns
// performance-live rows. Keep the latter when an indexed subset refresh is
// empty (as it legitimately is for Investing Accounts), and let an indexed row
// win when both sources publish the same position id.
function replaceIndexedPositionSubsetRows(input: {
  collection: CollectionRowsReader<PortfolioPositionModel> | undefined;
  fetchedRows: PortfolioPositionModel[];
  portfolioIds: ReadonlySet<string>;
}): PortfolioPositionModel[] {
  const performanceLiveRows = readCollectionRows(input.collection).filter(
    (row) =>
      input.portfolioIds.has(row.portfolioId) &&
      readPortfolioPositionSource(row) === PERFORMANCE_LIVE_POSITION_SOURCE,
  );

  return replaceSubsetRows({
    collection: input.collection,
    fetchedRows: [...performanceLiveRows, ...input.fetchedRows],
    matchesSubset: (row) => input.portfolioIds.has(row.portfolioId),
  });
}

function deleteStalePortfolioAssetUniverseRows(
  collections: PortfolioDomainCollections,
  portfolioId: string,
  nextLinks: PortfolioAssetUniverseModel[],
): void {
  const nextLinkIds = new Set(nextLinks.map((link) => link.id));
  const staleLinkIds = readCollectionRows(collections.portfolioAssetUniverse)
    .filter(
      (link) => link.portfolioId === portfolioId && !nextLinkIds.has(link.id),
    )
    .map((link) => link.id);

  if (staleLinkIds.length > 0) {
    collections.portfolioAssetUniverse.utils.writeDelete(staleLinkIds);
  }
}

function mergePerformanceLiveAssetPriceRows(
  collections: PortfolioDomainCollections,
  nextPrices: PortfolioAssetPriceModel[],
): PortfolioAssetPriceModel[] {
  const existingPricesByAssetId = new Map(
    readCollectionRows(collections.assetPrices).map((price) => [
      price.assetId,
      price,
    ]),
  );

  return nextPrices.map((price) => {
    const existingPrice = existingPricesByAssetId.get(price.assetId);
    if (!existingPrice) {
      return price;
    }

    return {
      ...existingPrice,
      priceUsd: price.priceUsd,
      source: price.source,
      updatedAt: price.updatedAt,
    };
  });
}

function deleteStalePerformanceLivePositions(
  collections: PortfolioDomainCollections,
  portfolioId: string,
  nextPositions: PortfolioPositionModel[],
): void {
  const nextPositionIds = new Set(nextPositions.map((position) => position.id));
  const stalePositionIds = readCollectionRows(collections.positions)
    .filter(
      (position) =>
        position.portfolioId === portfolioId &&
        readPortfolioPositionSource(position) ===
          PERFORMANCE_LIVE_POSITION_SOURCE &&
        !nextPositionIds.has(position.id),
    )
    .map((position) => position.id);

  if (stalePositionIds.length > 0) {
    collections.positions.utils.writeDelete(stalePositionIds);
  }
}

function getStalePortfolioLivePositionIds(
  collections: PortfolioDomainCollections,
  portfolioId: string,
  nextPositions: PortfolioPositionModel[],
): string[] {
  const nextPositionIds = new Set(nextPositions.map((position) => position.id));
  return readCollectionRows(collections.portfolioLivePositions)
    .filter(
      (position) =>
        position.portfolioId === portfolioId &&
        !nextPositionIds.has(position.id),
    )
    .map((position) => position.id);
}

function filterWritablePerformanceLivePositions(
  collections: PortfolioDomainCollections,
  nextPositions: PortfolioPositionModel[],
): PortfolioPositionModel[] {
  const existingPositionsById = new Map(
    readCollectionRows(collections.positions).map((position) => [
      position.id,
      position,
    ]),
  );

  return nextPositions.filter((position) => {
    const existingPosition = existingPositionsById.get(position.id);
    return (
      !existingPosition ||
      readPortfolioPositionSource(existingPosition) ===
        PERFORMANCE_LIVE_POSITION_SOURCE
    );
  });
}

function filterMissingPerformanceLiveAssetRows(
  collections: PortfolioDomainCollections,
  nextAssets: PortfolioAssetModel[],
): PortfolioAssetModel[] {
  const existingAssetIds = new Set(
    readCollectionRows(collections.assets).map((asset) => asset.id),
  );

  // Performance owns live balances and prices, which are written to their
  // dedicated collections below. Its embedded asset is only a bootstrap row
  // so a position can still join before normalized asset metadata arrives.
  // Once an asset exists, the normalized asset projection owns its identity
  // in full; do not write the performance row for that key at all.
  return nextAssets.filter((asset) => !existingAssetIds.has(asset.id));
}

function hasHydratedPortfolioAssetDisplayMetadata(
  asset: PortfolioAssetModel,
): boolean {
  if (asset.displayMetadataStatus === "metadata_unavailable") {
    return true;
  }

  return hasPortfolioAssetDisplayMetadataFields(asset);
}

function hasPortfolioAssetDisplayMetadataFields(
  asset: PortfolioAssetModel,
): boolean {
  const name = asset.name.trim();
  const symbol = asset.symbol.trim();
  const iconUrl = asset.iconUrl?.trim();

  return Boolean(iconUrl) || (name.length > 0 && name !== symbol);
}

function markUnavailablePortfolioAssetDisplayMetadata(
  asset: PortfolioAssetModel,
): PortfolioAssetModel {
  if (hasPortfolioAssetDisplayMetadataFields(asset)) {
    return asset;
  }

  return {
    ...asset,
    displayMetadataStatus: "metadata_unavailable",
  };
}

function mapWindowReturn(
  raw:
    | {
        absoluteChangeUsd?: string;
        returnPct?: string;
      }
    | null
    | undefined,
): PortfolioPerformanceWindowReturn | undefined {
  if (!raw) {
    return undefined;
  }

  const returnPct = coerceFiniteNumber(raw.returnPct);
  const absoluteChangeUsd = coerceFiniteNumber(raw.absoluteChangeUsd);
  if (returnPct === undefined && absoluteChangeUsd === undefined) {
    return undefined;
  }

  return {
    absoluteChangeUsd: absoluteChangeUsd ?? 0,
    returnPct: returnPct ?? 0,
  };
}

function mapPerformanceWindowStatsModel(
  portfolioId: string,
  payload: PortfolioPerformanceSeriesPayload | null,
): PortfolioPerformanceWindowStatsModel | null {
  const stats = payload?.stats;
  if (!stats) {
    return null;
  }

  const day = mapWindowReturn(stats.DAY);
  const week = mapWindowReturn(stats.WEEK);
  const month = mapWindowReturn(stats.MONTH);
  const year = mapWindowReturn(stats.YEAR);
  const allTime = mapWindowReturn(stats.ALL);

  if (!day && !week && !month && !year && !allTime) {
    return null;
  }

  return {
    allTime,
    day,
    id: portfolioId,
    month,
    portfolioId,
    updatedAt: Date.now(),
    week,
    year,
  };
}

function mapHistoryEventModels(
  portfolioId: string,
  rawHistory: PortfolioDetailNormalizedResponse["history"],
): PortfolioHistoryEventModel[] {
  const historyRecord = asRecord(rawHistory);
  if (!historyRecord) {
    return [];
  }

  const rawItems = Array.isArray(historyRecord.items)
    ? historyRecord.items
    : [];
  const mappedEvents: PortfolioHistoryEventModel[] = [];

  for (const [index, rawItem] of rawItems.entries()) {
    const item = asRecord(rawItem);
    if (!item) {
      continue;
    }

    const timestamp =
      readTimestampMs(item.timestamp) ??
      readTimestampMs(item.block_timestamp) ??
      Date.now();
    const eventType =
      readString(item.type) ?? readString(item.event_type) ?? "activity";
    const id =
      readString(item.id) ??
      `${portfolioId}:${timestamp}:${eventType}:${index}`;

    const amountUsd = coerceFiniteNumber(
      (typeof item.amount_usd === "number" ||
      typeof item.amount_usd === "string"
        ? item.amount_usd
        : typeof item.total_usd_value === "number" ||
            typeof item.total_usd_value === "string"
          ? item.total_usd_value
          : typeof item.from_total_usd_value === "number" ||
              typeof item.from_total_usd_value === "string"
            ? item.from_total_usd_value
            : undefined) ?? undefined,
    );

    const assetId =
      readString(item.asset_id) ??
      readString(item.from_asset_id) ??
      readString(item.to_asset_id);

    mappedEvents.push({
      amountUsd,
      assetId,
      eventType,
      id,
      payload: rawItem,
      portfolioId,
      timestamp,
    });
  }

  return mappedEvents;
}

function mapAssetPnlModels(
  portfolioId: string,
  rawAssetPnl: PortfolioDetailNormalizedResponse["assetPnl"],
): PortfolioAssetPnlModel[] {
  if (!Array.isArray(rawAssetPnl)) {
    return [];
  }

  const models: PortfolioAssetPnlModel[] = [];

  for (const [index, rawPnl] of rawAssetPnl.entries()) {
    const item = asRecord(rawPnl);
    if (!item) {
      continue;
    }

    const assetId = readString(item.assetId) ?? readString(item.asset_id);
    if (!assetId) {
      continue;
    }

    models.push({
      assetId,
      id: readString(item.id) ?? `${portfolioId}:${assetId}:${index}`,
      pnlUsd: coerceFiniteNumber(
        (typeof item.pnlUsd === "number" || typeof item.pnlUsd === "string"
          ? item.pnlUsd
          : typeof item.pnl_usd === "number" || typeof item.pnl_usd === "string"
            ? item.pnl_usd
            : undefined) ?? undefined,
      ),
      portfolioId,
      updatedAt: Date.now(),
    });
  }

  return models;
}

function deleteStalePortfolioPositionRows(input: {
  collections: PortfolioDomainCollections;
  fetchedRows: readonly PortfolioPositionModel[];
  portfolioIds: ReadonlySet<string>;
}) {
  const fetchedRowIds = new Set(input.fetchedRows.map((row) => row.id));
  const staleRowIds: string[] = [];
  for (const row of readCollectionRows(input.collections.positions)) {
    if (
      input.portfolioIds.has(row.portfolioId) &&
      readPortfolioPositionSource(row) !== PERFORMANCE_LIVE_POSITION_SOURCE &&
      !fetchedRowIds.has(row.id)
    ) {
      staleRowIds.push(row.id);
    }
  }

  if (staleRowIds.length > 0) {
    input.collections.positions.utils.writeDelete(staleRowIds);
  }
}

function deleteStalePortfolioVaultRows(input: {
  collections: PortfolioDomainCollections;
  fetchedRows: readonly PortfolioVaultModel[];
  portfolioIds: ReadonlySet<string>;
}) {
  const fetchedRowIds = new Set(input.fetchedRows.map((row) => row.id));
  const staleRowIds: string[] = [];
  for (const row of readCollectionRows(input.collections.vaults)) {
    if (input.portfolioIds.has(row.portfolioId) && !fetchedRowIds.has(row.id)) {
      staleRowIds.push(row.id);
    }
  }

  if (staleRowIds.length > 0) {
    input.collections.vaults.utils.writeDelete(staleRowIds);
  }
}

function deleteStaleDashboardAssetSummaryRows(input: {
  collections: PortfolioDomainCollections;
  fetchedRows: readonly PortfolioDashboardAssetSummaryModel[];
  portfolioIds: ReadonlySet<string>;
}) {
  const fetchedRowIds = new Set(input.fetchedRows.map((row) => row.id));
  const staleRowIds: string[] = [];
  for (const row of readCollectionRows(
    input.collections.dashboardAssetSummaries,
  )) {
    if (input.portfolioIds.has(row.portfolioId) && !fetchedRowIds.has(row.id)) {
      staleRowIds.push(row.id);
    }
  }

  if (staleRowIds.length > 0) {
    input.collections.dashboardAssetSummaries.utils.writeDelete(staleRowIds);
  }
}

export function deleteStalePortfolioBoundSummaryRows(input: {
  collections: PortfolioDomainCollections;
  include: Pick<
    PortfolioSummaryHydrationInclude,
    "dashboardAssetSummaries" | "positions" | "vaults"
  >;
  payload: DashboardNormalizedPayload;
  portfolioIds: ReadonlySet<string>;
}) {
  if (input.include.dashboardAssetSummaries) {
    deleteStaleDashboardAssetSummaryRows({
      collections: input.collections,
      fetchedRows: input.payload.dashboardAssetSummaries ?? [],
      portfolioIds: input.portfolioIds,
    });
  }

  if (input.include.positions) {
    deleteStalePortfolioPositionRows({
      collections: input.collections,
      fetchedRows: input.payload.positions ?? [],
      portfolioIds: input.portfolioIds,
    });
  }

  if (input.include.vaults) {
    deleteStalePortfolioVaultRows({
      collections: input.collections,
      fetchedRows: input.payload.vaults ?? [],
      portfolioIds: input.portfolioIds,
    });
  }
}

export function applyDashboardPayloadToCollections(
  collections: PortfolioDomainCollections,
  payload: DashboardNormalizedPayload,
  principalOwnerAddressSet: ReadonlySet<string>,
) {
  const cashSummaryByPortfolioId = new Map(
    (payload.cashSummaries ?? []).map((cashSummary) => [
      cashSummary.portfolioId,
      cashSummary as PortfolioCashSummaryModel,
    ]),
  );
  const portfolioInstances = (payload.portfolioInstances ?? []).map(
    (portfolio) => {
      const normalized = normalizePortfolioInstanceModel(portfolio);
      return applyOwnershipFallback(
        {
          ...normalized,
          cashSummary:
            cashSummaryByPortfolioId.get(normalized.id) ??
            normalized.cashSummary ??
            null,
        },
        principalOwnerAddressSet,
      );
    },
  );

  if (portfolioInstances.length > 0) {
    collections.portfolioInstances.utils.writeUpsert(portfolioInstances);
  }
  if ((payload.positions?.length ?? 0) > 0) {
    collections.positions.utils.writeUpsert(payload.positions ?? []);
  }
  if ((payload.vaults?.length ?? 0) > 0) {
    collections.vaults.utils.writeUpsert(payload.vaults ?? []);
  }
  if ((payload.assets?.length ?? 0) > 0) {
    collections.assets.utils.writeUpsert(payload.assets ?? []);
  }
  if ((payload.assetPrices?.length ?? 0) > 0) {
    collections.assetPrices.utils.writeUpsert(payload.assetPrices ?? []);
  }
  if ((payload.dashboardAssetSummaries?.length ?? 0) > 0) {
    collections.dashboardAssetSummaries.utils.writeUpsert(
      payload.dashboardAssetSummaries ?? [],
    );
  }
  for (const performanceSeriesPayload of payload.performanceSeries ?? []) {
    applyPerformanceSeriesPayloadToCollections(
      collections,
      performanceSeriesPayload.portfolioId,
      performanceSeriesPayload,
    );
  }
}

export function applyDetailPayloadToCollections(
  collections: PortfolioDomainCollections,
  strategyInstanceId: string,
  payload: PortfolioDetailNormalizedResponse,
  options: {
    includeAssetPnl: boolean;
    includeAssetUniverse: boolean;
    includePerformanceSummary: boolean;
  },
) {
  if (options.includeAssetUniverse) {
    const assetUniverseLinks = mapPortfolioAssetUniverseModels(
      strategyInstanceId,
      payload.assets,
    );
    deleteStalePortfolioAssetUniverseRows(
      collections,
      strategyInstanceId,
      assetUniverseLinks,
    );
    if (assetUniverseLinks.length > 0) {
      collections.portfolioAssetUniverse.utils.writeUpsert(assetUniverseLinks);
    }
  }

  const scheduleModel = mapScheduleModel(strategyInstanceId, payload.schedule);
  if (scheduleModel) {
    collections.portfolioSchedules.utils.writeUpsert(scheduleModel);
  }

  const performancePoints = mapPerformancePointModels(
    strategyInstanceId,
    payload.performancePoints,
  );
  if (performancePoints.length > 0) {
    collections.performancePoints.utils.writeUpsert(performancePoints);
  }

  const performanceSummary = mapPerformanceSummaryModel(
    strategyInstanceId,
    payload.performanceSummary,
    payload.portfolioSavings,
  );
  if (performanceSummary) {
    collections.performanceSummaries.utils.writeUpsert(performanceSummary);
  } else if (options.includePerformanceSummary) {
    const hasExistingPerformanceSummary = readCollectionRows(
      collections.performanceSummaries,
    ).some((row) => row.id === strategyInstanceId);
    if (hasExistingPerformanceSummary) {
      collections.performanceSummaries.utils.writeDelete([strategyInstanceId]);
    }
  }

  const historyEvents = mapHistoryEventModels(
    strategyInstanceId,
    payload.history,
  );
  if (historyEvents.length > 0) {
    collections.historyEvents.utils.writeUpsert(historyEvents);
  }

  const assetPnl = mapAssetPnlModels(strategyInstanceId, payload.assetPnl);
  if (options.includeAssetPnl) {
    const fetchedAssetPnlIds = new Set(assetPnl.map((row) => row.id));
    const staleAssetPnlIds = readCollectionRows(collections.assetPnl)
      .filter(
        (row) =>
          row.portfolioId === strategyInstanceId &&
          !fetchedAssetPnlIds.has(row.id),
      )
      .map((row) => row.id);
    if (staleAssetPnlIds.length > 0) {
      collections.assetPnl.utils.writeDelete(staleAssetPnlIds);
    }
  }
  if (assetPnl.length > 0) {
    collections.assetPnl.utils.writeUpsert(assetPnl);
  }
}

export function applyPerformanceSeriesPayloadToCollections(
  collections: PortfolioDomainCollections,
  strategyInstanceId: string,
  payload: PortfolioPerformanceSeriesPayload | null,
) {
  const performanceSeries = mapPerformanceSeriesModels(
    strategyInstanceId,
    payload,
  );
  if (performanceSeries.length > 0) {
    collections.performanceSeries.utils.writeUpsert(performanceSeries);
  }

  const windowStats = mapPerformanceWindowStatsModel(
    strategyInstanceId,
    payload,
  );
  if (windowStats) {
    collections.performanceWindowStats.utils.writeUpsert(windowStats);
  }

  const liveAssetModels = mapPerformanceLiveAssetCollectionModels(
    strategyInstanceId,
    payload,
  );
  const writableLivePositions = filterWritablePerformanceLivePositions(
    collections,
    liveAssetModels.positions,
  );
  const hasLiveAssetsSnapshot = hasPerformanceLiveAssetsSnapshot(payload);
  if (hasLiveAssetsSnapshot) {
    deleteStalePerformanceLivePositions(
      collections,
      strategyInstanceId,
      writableLivePositions,
    );
  }
  const missingLiveAssets = filterMissingPerformanceLiveAssetRows(
    collections,
    liveAssetModels.assets,
  );
  if (missingLiveAssets.length > 0) {
    collections.assets.utils.writeUpsert(missingLiveAssets);
  }
  if (liveAssetModels.assetPrices.length > 0) {
    collections.assetPrices.utils.writeUpsert(
      mergePerformanceLiveAssetPriceRows(
        collections,
        liveAssetModels.assetPrices,
      ),
    );
  }
  if (writableLivePositions.length > 0) {
    collections.positions.utils.writeUpsert(writableLivePositions);
  }

  const staleLivePositionIds = hasLiveAssetsSnapshot
    ? getStalePortfolioLivePositionIds(
        collections,
        strategyInstanceId,
        liveAssetModels.positions,
      )
    : [];
  if (staleLivePositionIds.length > 0 || liveAssetModels.positions.length > 0) {
    // A performance refresh replaces the authoritative live-position set.
    // Publish the delete + upsert as one collection commit so live-query
    // consumers never observe the half-replaced (often empty) intermediate
    // state and misclassify a funded account as having no assets.
    collections.portfolioLivePositions.utils.writeBatch(() => {
      if (staleLivePositionIds.length > 0) {
        collections.portfolioLivePositions.utils.writeDelete(
          staleLivePositionIds,
        );
      }
      if (liveAssetModels.positions.length > 0) {
        collections.portfolioLivePositions.utils.writeUpsert(
          liveAssetModels.positions,
        );
      }
    });
  }
}

export function applyPortfolioScheduledFunctionsPayloadToCollections(
  collections: PortfolioDomainCollections,
  portfolioId: string,
  payload: PortfolioScheduledFunctionsListResponse | null,
) {
  if (payload === null) {
    return;
  }

  const scheduledFunctions = mapPortfolioScheduledFunctionModels(
    portfolioId,
    payload,
  );
  if (scheduledFunctions.length > 0) {
    collections.portfolioScheduledFunctions.utils.writeUpsert(
      scheduledFunctions,
    );
    return;
  }

  const existingScheduledFunctionIds: string[] = [];
  for (const scheduledFunction of readCollectionRows(
    collections.portfolioScheduledFunctions,
  )) {
    if (scheduledFunction.portfolioId === portfolioId) {
      existingScheduledFunctionIds.push(scheduledFunction.id);
    }
  }

  if (existingScheduledFunctionIds.length > 0) {
    collections.portfolioScheduledFunctions.utils.writeDelete(
      existingScheduledFunctionIds,
    );
  }
}

export interface PortfolioCollectionDefinitionRuntime {
  getInFlightPortfolioDashboardSeedPromise: (input: {
    ownerAddresses?: string[];
    sessionScopeToken?: string;
  }) => Promise<void> | undefined;
  haveHydratedOwnerPortfolioListRows: (
    portfolioRows: PortfolioInstanceModel[],
    options?: { ownerAddresses?: string[]; sessionScopeToken?: string },
  ) => boolean;
  havePortfolioSummaryComponentsHydrated: (
    strategyInstanceIds: string[],
    components: Array<
      | "assetPrices"
      | "assets"
      | "dashboardAssetSummaries"
      | "positions"
      | "vaults"
    >,
    options?: { ownerAddresses?: string[]; sessionScopeToken?: string },
  ) => boolean;
}

function toOwnersCacheKey(ownerAddresses: string[] | undefined): string {
  if (!ownerAddresses || ownerAddresses.length === 0) {
    return "__owners:none__";
  }

  return ownerAddresses.join("|");
}

export function toBrowserCollectionCacheKey(input: {
  ownerAddresses?: string[];
  sessionScopeToken?: string;
}): string {
  const sessionScopeToken =
    input.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  return `${sessionScopeToken}::${toOwnersCacheKey(input.ownerAddresses)}`;
}

export function createPortfolioDomainCollectionDefinitions(
  dependencies: PortfolioDbDependencies,
  runtime: PortfolioCollectionDefinitionRuntime,
) {
  const principalOwnerAddresses = uniqueNormalizedAddresses(
    dependencies.ownerAddresses ?? [],
  );
  const principalOwnerAddressSet = new Set(principalOwnerAddresses);
  const scopedSessionToken =
    dependencies.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const scopedCollectionToken = toBrowserCollectionCacheKey({
    ownerAddresses: principalOwnerAddresses,
    sessionScopeToken: scopedSessionToken,
  });
  const collectionQueryScope = {
    ownerAddresses: principalOwnerAddresses,
    sessionScopeToken: scopedSessionToken,
  };
  let portfolioInstancesRef:
    | CollectionRowsReader<PortfolioInstanceModel>
    | undefined;
  let positionsRef: CollectionRowsReader<PortfolioPositionModel> | undefined;
  let portfolioAssetUniverseRef:
    | CollectionRowsReader<PortfolioAssetUniverseModel>
    | undefined;
  let portfolioLivePositionsRef:
    | CollectionRowsReader<PortfolioPositionModel>
    | undefined;
  let vaultsRef: CollectionRowsReader<PortfolioVaultModel> | undefined;
  let assetsRef: CollectionRowsUpserter<PortfolioAssetModel> | undefined;
  let assetPricesRef:
    | CollectionRowsReader<PortfolioAssetPriceModel>
    | undefined;
  let dashboardAssetSummariesRef:
    | CollectionRowsReader<PortfolioDashboardAssetSummaryModel>
    | undefined;
  let portfolioSchedulesRef:
    | CollectionRowsReader<PortfolioScheduleModel>
    | undefined;
  let portfolioWorkflowsRef:
    | CollectionRowsReader<PortfolioWorkflowModel>
    | undefined;
  let portfolioScheduledFunctionsRef:
    | CollectionRowsReader<PortfolioScheduledFunctionModel>
    | undefined;
  let performancePointsRef:
    | CollectionRowsReader<PortfolioPerformancePointModel>
    | undefined;
  let performanceSeriesRef:
    | CollectionRowsReader<PortfolioPerformanceSeriesPointModel>
    | undefined;
  let performanceSummariesRef:
    | CollectionRowsReader<PortfolioPerformanceSummaryModel>
    | undefined;
  let performanceWindowStatsRef:
    | CollectionRowsReader<PortfolioPerformanceWindowStatsModel>
    | undefined;
  let historyEventsRef:
    | CollectionRowsReader<PortfolioHistoryEventModel>
    | undefined;
  let assetPnlRef: CollectionRowsReader<PortfolioAssetPnlModel> | undefined;
  let allocationHistoryCheckpointsRef:
    | CollectionRowsReader<PortfolioAllocationHistoryCheckpointModel>
    | undefined;

  const portfolioInstances = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-portfolio-instances",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioInstanceModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters, limit } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);

        if (strategyInstanceIds.length > 0) {
          const strategyInstanceIdSet = new Set(strategyInstanceIds);
          const payload = await queryDashboardNormalizedData(dependencies, {
            archiveStatus: "all",
            include: {
              assetPrices: false,
              assets: false,
              portfolioAssetBalances: false,
              portfolioInstances: true,
              positions: false,
              vaults: false,
            },
            strategyInstanceIds,
          });

          const fetchedPortfolios = (payload.portfolioInstances ?? []).map(
            (portfolio) =>
              applyOwnershipFallback(
                normalizePortfolioInstanceModel(portfolio),
                principalOwnerAddressSet,
              ),
          );

          return replaceSubsetRows({
            collection: portfolioInstancesRef,
            fetchedRows: fetchedPortfolios,
            matchesSubset: (row) => strategyInstanceIdSet.has(row.id),
          });
        }

        const archivedFilter = extractEqFilterValue<boolean>(
          filters,
          "archived",
        );
        const archiveStatus =
          archivedFilter === undefined
            ? "all"
            : archivedFilter
              ? "archived"
              : "active";

        const ownerAddressFilters = extractOwnerAddresses(filters);
        const existingPortfolioRows = readCollectionRows(portfolioInstancesRef);
        if (
          existingPortfolioRows.length > 0 &&
          runtime.haveHydratedOwnerPortfolioListRows(existingPortfolioRows, {
            ownerAddresses: principalOwnerAddresses,
            sessionScopeToken: scopedSessionToken,
          })
        ) {
          return existingPortfolioRows;
        }

        const dashboardSeedPromise =
          runtime.getInFlightPortfolioDashboardSeedPromise({
            ownerAddresses: principalOwnerAddresses,
            sessionScopeToken: scopedSessionToken,
          });
        if (dashboardSeedPromise) {
          await dashboardSeedPromise.catch(() => undefined);

          const seededPortfolioRows = readCollectionRows(portfolioInstancesRef);
          if (seededPortfolioRows.length > 0) {
            return seededPortfolioRows;
          }
        }

        const payload = await queryDashboardNormalizedData(dependencies, {
          archiveStatus,
          include: {
            assetPrices: false,
            assets: false,
            portfolioAssetBalances: false,
            portfolioInstances: true,
            positions: false,
            vaults: false,
          },
          limit: limit ?? 100,
          ownerAddress:
            ownerAddressFilters.length === 1
              ? ownerAddressFilters[0]
              : undefined,
        });

        const filteredPortfolios = (payload.portfolioInstances ?? []).filter(
          (portfolio) =>
            ownerAddressFilters.length === 0 ||
            ownerAddressFilters.includes(
              normalizePortfolioAddress(portfolio.ownerAddress),
            ),
        );

        const normalizedPortfolioRows = filteredPortfolios.map((portfolio) =>
          applyOwnershipFallback(
            normalizePortfolioInstanceModel(portfolio),
            principalOwnerAddressSet,
          ),
        );
        return normalizedPortfolioRows;
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.portfolioInstances,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 30_000,
      syncMode: "on-demand",
    }),
  );
  portfolioInstancesRef = portfolioInstances;
  portfolioInstances.createIndex((row) => row.ownerAddress);
  portfolioInstances.createIndex((row) => row.archived);

  const positions = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-positions", scopedCollectionToken),
      getKey: (item: PortfolioPositionModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);

        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(positionsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const dashboardSeedPromise =
          runtime.getInFlightPortfolioDashboardSeedPromise({
            ownerAddresses: principalOwnerAddresses,
            sessionScopeToken: scopedSessionToken,
          });
        if (dashboardSeedPromise) {
          await dashboardSeedPromise.catch(() => undefined);
        }
        if (
          runtime.havePortfolioSummaryComponentsHydrated(
            strategyInstanceIds,
            ["positions"],
            {
              ownerAddresses: principalOwnerAddresses,
              sessionScopeToken: scopedSessionToken,
            },
          )
        ) {
          return readCollectionRows(positionsRef);
        }

        const chunks = chunkArray(strategyInstanceIds, STRATEGY_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.map((strategyChunk) =>
            queryDashboardNormalizedData(dependencies, {
              archiveStatus: "all",
              include: {
                assetPrices: false,
                assets: false,
                portfolioAssetBalances: false,
                portfolioInstances: false,
                positions: true,
                vaults: false,
              },
              strategyInstanceIds: strategyChunk,
            }),
          ),
        );

        return replaceIndexedPositionSubsetRows({
          collection: positionsRef,
          fetchedRows: mergeNormalizedPayloads(responses).positions ?? [],
          portfolioIds: strategyInstanceIdSet,
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.positions,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  positionsRef = positions;
  positions.createIndex((row) => row.portfolioId);
  positions.createIndex((row) => row.assetId);

  const portfolioAssetUniverse = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-asset-universe",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioAssetUniverseModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);

        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(portfolioAssetUniverseRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        return replaceSubsetRows({
          collection: portfolioAssetUniverseRef,
          fetchedRows: readCollectionRows(portfolioAssetUniverseRef).filter(
            (link) => strategyInstanceIdSet.has(link.portfolioId),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.assetUniverse,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  portfolioAssetUniverseRef = portfolioAssetUniverse;
  portfolioAssetUniverse.createIndex((row) => row.portfolioId);
  portfolioAssetUniverse.createIndex((row) => row.assetId);

  const portfolioLivePositions = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-live-positions",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioPositionModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);

        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(portfolioLivePositionsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        return replaceSubsetRows({
          collection: portfolioLivePositionsRef,
          fetchedRows: readCollectionRows(portfolioLivePositionsRef).filter(
            (position) => strategyInstanceIdSet.has(position.portfolioId),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.livePositions,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  portfolioLivePositionsRef = portfolioLivePositions;
  portfolioLivePositions.createIndex((row) => row.portfolioId);
  portfolioLivePositions.createIndex((row) => row.assetId);

  const dashboardAssetSummaries = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-dashboard-asset-summaries",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioDashboardAssetSummaryModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);

        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(dashboardAssetSummariesRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const dashboardSeedPromise =
          runtime.getInFlightPortfolioDashboardSeedPromise({
            ownerAddresses: principalOwnerAddresses,
            sessionScopeToken: scopedSessionToken,
          });
        if (dashboardSeedPromise) {
          await dashboardSeedPromise.catch(() => undefined);
        }
        if (
          runtime.havePortfolioSummaryComponentsHydrated(
            strategyInstanceIds,
            ["dashboardAssetSummaries"],
            {
              ownerAddresses: principalOwnerAddresses,
              sessionScopeToken: scopedSessionToken,
            },
          )
        ) {
          return readCollectionRows(dashboardAssetSummariesRef);
        }

        const chunks = chunkArray(strategyInstanceIds, STRATEGY_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.map((strategyChunk) =>
            queryDashboardNormalizedData(dependencies, {
              archiveStatus: "all",
              include: {
                assetPrices: false,
                assets: false,
                dashboardAssetSummaries: true,
                portfolioAssetBalances: false,
                portfolioInstances: false,
                positions: false,
                vaults: false,
              },
              strategyInstanceIds: strategyChunk,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: dashboardAssetSummariesRef,
          fetchedRows:
            mergeNormalizedPayloads(responses).dashboardAssetSummaries ?? [],
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.dashboardAssetSummaries,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  dashboardAssetSummariesRef = dashboardAssetSummaries;
  dashboardAssetSummaries.createIndex((row) => row.portfolioId);
  dashboardAssetSummaries.createIndex((row) => row.displayKind);
  dashboardAssetSummaries.createIndex((row) => row.assetCanonicalId ?? null);

  const vaults = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-vaults", scopedCollectionToken),
      getKey: (item: PortfolioVaultModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        const vaultIds = new Set(extractVaultIds(filters));

        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(vaultsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const dashboardSeedPromise =
          runtime.getInFlightPortfolioDashboardSeedPromise({
            ownerAddresses: principalOwnerAddresses,
            sessionScopeToken: scopedSessionToken,
          });
        if (dashboardSeedPromise) {
          await dashboardSeedPromise.catch(() => undefined);
        }
        if (
          runtime.havePortfolioSummaryComponentsHydrated(
            strategyInstanceIds,
            ["vaults"],
            {
              ownerAddresses: principalOwnerAddresses,
              sessionScopeToken: scopedSessionToken,
            },
          )
        ) {
          return readCollectionRows(vaultsRef);
        }

        const chunks = chunkArray(strategyInstanceIds, STRATEGY_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.map((strategyChunk) =>
            queryDashboardNormalizedData(dependencies, {
              archiveStatus: "all",
              include: {
                assetPrices: false,
                assets: false,
                portfolioAssetBalances: false,
                portfolioInstances: false,
                positions: false,
                vaults: true,
              },
              strategyInstanceIds: strategyChunk,
            }),
          ),
        );

        const mergedVaults = mergeNormalizedPayloads(responses).vaults ?? [];
        if (vaultIds.size === 0) {
          return replaceSubsetRows({
            collection: vaultsRef,
            fetchedRows: mergedVaults,
            matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
          });
        }

        return replaceSubsetRows({
          collection: vaultsRef,
          fetchedRows: mergedVaults.filter(
            (vault) => vaultIds.has(vault.id) || vaultIds.has(vault.accountId),
          ),
          matchesSubset: (row) =>
            vaultIds.has(row.id) || vaultIds.has(row.accountId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.vaults,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 30_000,
      syncMode: "on-demand",
    }),
  );
  vaultsRef = vaults;
  vaults.createIndex((row) => row.portfolioId);

  const assets = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-assets", scopedCollectionToken),
      getKey: (item: PortfolioAssetModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const assetIds = extractAssetIds(filters);

        if (assetIds.length === 0) {
          return readCollectionRows(assetsRef);
        }

        const assetIdSet = new Set(assetIds);
        const existingAssetRows = readCollectionRows(assetsRef).filter((row) =>
          assetIdSet.has(row.id),
        );
        if (
          existingAssetRows.length === assetIdSet.size &&
          existingAssetRows.every(hasHydratedPortfolioAssetDisplayMetadata)
        ) {
          return readCollectionRows(assetsRef);
        }

        const chunks = chunkArray(assetIds, ASSET_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.map((assetIdsChunk) =>
            queryDashboardNormalizedData(dependencies, {
              include: {
                assetPrices: false,
                assets: true,
                portfolioAssetBalances: false,
                portfolioInstances: false,
                positions: false,
                vaults: false,
              },
              assetIds: assetIdsChunk,
            }),
          ),
        );

        const fetchedAssets = (
          mergeNormalizedPayloads(responses).assets ?? []
        ).map(markUnavailablePortfolioAssetDisplayMetadata);
        const fetchedAssetIds = new Set(fetchedAssets.map((asset) => asset.id));
        const retainedExistingAssets = existingAssetRows
          .filter((asset) => !fetchedAssetIds.has(asset.id))
          .map(markUnavailablePortfolioAssetDisplayMetadata);
        const subsetAssets = [...retainedExistingAssets, ...fetchedAssets];
        if (subsetAssets.length > 0) {
          // Keep active live queries in sync with the metadata result before
          // collection reconciliation replaces the queried subset.
          assetsRef?.utils.writeUpsert(subsetAssets);
        }

        return replaceSubsetRows({
          collection: assetsRef,
          fetchedRows: subsetAssets,
          matchesSubset: (row) => assetIdSet.has(row.id),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.assets,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 300_000,
      syncMode: "on-demand",
    }),
  );
  assetsRef = assets;
  assets.createIndex((row) => row.id);

  const assetPrices = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-asset-prices", scopedCollectionToken),
      getKey: (item: PortfolioAssetPriceModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const assetIds = extractAssetIds(filters);

        if (assetIds.length === 0) {
          return readCollectionRows(assetPricesRef);
        }

        const assetIdSet = new Set(assetIds);
        const chunks = chunkArray(assetIds, ASSET_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.map((assetIdsChunk) =>
            queryDashboardNormalizedData(dependencies, {
              include: {
                assetPrices: true,
                assets: false,
                portfolioAssetBalances: false,
                portfolioInstances: false,
                positions: false,
                vaults: false,
              },
              assetIds: assetIdsChunk,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: assetPricesRef,
          fetchedRows: mergeNormalizedPayloads(responses).assetPrices ?? [],
          matchesSubset: (row) => assetIdSet.has(row.assetId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.assetPrices,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  assetPricesRef = assetPrices;
  assetPrices.createIndex((row) => row.assetId);

  const portfolioSchedules = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-schedules", scopedCollectionToken),
      getKey: (item: PortfolioScheduleModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(portfolioSchedulesRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const chunks = chunkArray(strategyInstanceIds, DETAIL_BATCH_LIMIT);
        const responses = await Promise.all(
          chunks.flatMap((chunk) =>
            chunk.map((strategyInstanceId) =>
              queryPortfolioDetailNormalizedData(dependencies, {
                include: {
                  assetPnl: false,
                  assetPrices: false,
                  assets: false,
                  history: false,
                  performancePoints: false,
                  performanceSummary: false,
                  portfolioInstances: false,
                  positions: false,
                  schedule: true,
                  vaults: false,
                },
                returnMethod: PerformanceReturnMethod.MWR,
                strategyInstanceId,
              }),
            ),
          ),
        );

        return replaceSubsetRows({
          collection: portfolioSchedulesRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) => {
              const model = mapScheduleModel(
                response.strategyInstanceId,
                response.schedule,
              );
              return model ? [model] : [];
            }),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.schedules,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  portfolioSchedulesRef = portfolioSchedules;
  portfolioSchedules.createIndex((row) => row.portfolioId);

  const portfolioWorkflows = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-workflows", scopedCollectionToken),
      getKey: (item: PortfolioWorkflowModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters, limit } = parseSubsetFilters(context.meta);
        const portfolioIds = extractPortfolioIds(filters);
        const kind = readPortfolioWorkflowListKind(
          extractEqFilterValue<unknown>(filters, "kind"),
        );
        const status = readPortfolioWorkflowListStatus(
          extractEqFilterValue<unknown>(filters, "status"),
        );
        if (portfolioIds.length === 0) {
          return readCollectionRows(portfolioWorkflowsRef);
        }

        const portfolioIdSet = new Set(portfolioIds);
        const responses = await Promise.all(
          portfolioIds.map(async (portfolioId) => ({
            payload: await queryPortfolioWorkflowsData(dependencies, {
              kind,
              limit: limit ?? 50,
              portfolioId,
              status,
            }),
            portfolioId,
          })),
        );

        return replaceSubsetRows({
          collection: portfolioWorkflowsRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapPortfolioWorkflowModels(
                response.portfolioId,
                response.payload,
              ),
            ),
          ),
          matchesSubset: (row) =>
            portfolioIdSet.has(row.portfolioId) &&
            (!kind || row.kind === kind) &&
            (!status || row.status === status),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.workflows,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  portfolioWorkflowsRef = portfolioWorkflows;
  portfolioWorkflows.createIndex((row) => row.portfolioId);
  portfolioWorkflows.createIndex((row) => row.workflowId);

  const portfolioScheduledFunctions = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-scheduled-functions",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioScheduledFunctionModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const portfolioIds = extractPortfolioIds(filters);
        const functionKey = readNonEmptyString(
          extractEqFilterValue<unknown>(filters, "functionKey"),
        );
        if (portfolioIds.length === 0) {
          return readCollectionRows(portfolioScheduledFunctionsRef);
        }

        const portfolioIdSet = new Set(portfolioIds);
        const responses = await Promise.all(
          portfolioIds.map(async (portfolioId) => ({
            payload: await queryPortfolioScheduledFunctionsData(
              dependencies,
              portfolioId,
            ),
            portfolioId,
          })),
        );

        return replaceSubsetRows({
          collection: portfolioScheduledFunctionsRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapPortfolioScheduledFunctionModels(
                response.portfolioId,
                response.payload,
              ),
            ),
          ).filter(
            (row) =>
              portfolioIdSet.has(row.portfolioId) &&
              (!functionKey || row.functionKey === functionKey),
          ),
          matchesSubset: (row) =>
            portfolioIdSet.has(row.portfolioId) &&
            (!functionKey || row.functionKey === functionKey),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.scheduledFunctions,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  portfolioScheduledFunctionsRef = portfolioScheduledFunctions;
  portfolioScheduledFunctions.createIndex((row) => row.portfolioId);
  portfolioScheduledFunctions.createIndex((row) => row.scheduleId);

  const performancePoints = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-performance-points",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioPerformancePointModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(performancePointsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const responses = await Promise.all(
          strategyInstanceIds.map((strategyInstanceId) =>
            queryPortfolioDetailNormalizedData(dependencies, {
              include: {
                assetPnl: false,
                assetPrices: false,
                assets: false,
                history: false,
                performancePoints: true,
                performanceSummary: false,
                portfolioInstances: false,
                positions: false,
                schedule: false,
                vaults: false,
              },
              returnMethod: PerformanceReturnMethod.MWR,
              strategyInstanceId,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: performancePointsRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapPerformancePointModels(
                response.strategyInstanceId,
                response.performancePoints,
              ),
            ),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.performancePoints,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  performancePointsRef = performancePoints;
  performancePoints.createIndex((row) => row.portfolioId);

  const performanceWindowStats = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-performance-window-stats",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioPerformanceWindowStatsModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(performanceWindowStatsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        return readCollectionRows(performanceWindowStatsRef).filter((row) =>
          strategyInstanceIdSet.has(row.portfolioId),
        );
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.performanceWindowStats,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  performanceWindowStatsRef = performanceWindowStats;
  performanceWindowStats.createIndex((row) => row.portfolioId);

  const performanceSeries = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-performance-series",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioPerformanceSeriesPointModel) => item.id,
      queryClient: dependencies.queryClient,
      // Sparklines are stale-while-revalidate UI data. Explicit hydrators write
      // fresh rows; the collection query itself only exposes the retained cache
      // so a background sync cannot replace visible charts with an empty subset.
      queryFn: async () => readCollectionRows(performanceSeriesRef),
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.performanceSeries,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: Number.POSITIVE_INFINITY,
      syncMode: "on-demand",
    }),
  );
  performanceSeriesRef = performanceSeries;
  performanceSeries.createIndex((row) => row.portfolioId);

  const performanceSummaries = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-performance-summaries",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioPerformanceSummaryModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(performanceSummariesRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const responses = await Promise.all(
          strategyInstanceIds.map((strategyInstanceId) =>
            queryPortfolioDetailNormalizedData(dependencies, {
              include: {
                assetPnl: false,
                assetPrices: false,
                assets: false,
                history: false,
                performancePoints: false,
                performanceSummary: true,
                portfolioInstances: false,
                positions: false,
                schedule: false,
                vaults: false,
              },
              returnMethod: PerformanceReturnMethod.MWR,
              strategyInstanceId,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: performanceSummariesRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) => {
              const model = mapPerformanceSummaryModel(
                response.strategyInstanceId,
                response.performanceSummary,
                response.portfolioSavings,
              );
              return model ? [model] : [];
            }),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.performanceSummaries,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  performanceSummariesRef = performanceSummaries;
  performanceSummaries.createIndex((row) => row.portfolioId);

  const historyEvents = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-history-events",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioHistoryEventModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters, limit } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(historyEventsRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const responses = await Promise.all(
          strategyInstanceIds.map((strategyInstanceId) =>
            queryPortfolioDetailNormalizedData(dependencies, {
              history: {
                includeEmptyRebalances: false,
                limit: limit ?? 30,
              },
              include: {
                assetPnl: false,
                assetPrices: false,
                assets: false,
                history: true,
                performancePoints: false,
                performanceSummary: false,
                portfolioInstances: false,
                positions: false,
                schedule: false,
                vaults: false,
              },
              returnMethod: PerformanceReturnMethod.MWR,
              strategyInstanceId,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: historyEventsRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapHistoryEventModels(
                response.strategyInstanceId,
                response.history,
              ),
            ),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.historyEvents,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 15_000,
      syncMode: "on-demand",
    }),
  );
  historyEventsRef = historyEvents;
  historyEvents.createIndex((row) => row.portfolioId);

  const assetPnl = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId("portfolio-asset-pnl", scopedCollectionToken),
      getKey: (item: PortfolioAssetPnlModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const strategyInstanceIds = extractPortfolioIds(filters);
        if (strategyInstanceIds.length === 0) {
          return readCollectionRows(assetPnlRef);
        }

        const strategyInstanceIdSet = new Set(strategyInstanceIds);
        const responses = await Promise.all(
          strategyInstanceIds.map((strategyInstanceId) =>
            queryPortfolioDetailNormalizedData(dependencies, {
              include: {
                assetPnl: true,
                assetPrices: false,
                assets: false,
                history: false,
                performancePoints: false,
                performanceSummary: false,
                portfolioInstances: false,
                positions: false,
                schedule: false,
                vaults: false,
              },
              returnMethod: PerformanceReturnMethod.MWR,
              strategyInstanceId,
            }),
          ),
        );

        return replaceSubsetRows({
          collection: assetPnlRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapAssetPnlModels(response.strategyInstanceId, response.assetPnl),
            ),
          ),
          matchesSubset: (row) => strategyInstanceIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.assetPnl,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 30_000,
      syncMode: "on-demand",
    }),
  );
  assetPnlRef = assetPnl;
  assetPnl.createIndex((row) => row.portfolioId);

  const allocationHistoryCheckpoints = createCollection(
    queryCollectionOptions({
      defaultIndexType: BasicIndex,
      id: toScopedCollectionId(
        "portfolio-allocation-history-checkpoints",
        scopedCollectionToken,
      ),
      getKey: (item: PortfolioAllocationHistoryCheckpointModel) => item.id,
      queryClient: dependencies.queryClient,
      queryFn: async (context) => {
        const { filters } = parseSubsetFilters(context.meta);
        const portfolioIds = extractPortfolioIds(filters);
        if (portfolioIds.length === 0) {
          return readCollectionRows(allocationHistoryCheckpointsRef);
        }

        const portfolioIdSet = new Set(portfolioIds);
        const responses = await Promise.all(
          portfolioIds.map(async (portfolioId) => ({
            payload: await queryPortfolioAllocationHistoryChartData(
              dependencies,
              portfolioId,
            ),
            portfolioId,
          })),
        );

        return replaceSubsetRows({
          collection: allocationHistoryCheckpointsRef,
          fetchedRows: dedupeById(
            responses.flatMap((response) =>
              mapAllocationHistoryCheckpointModels(
                response.portfolioId,
                response.payload,
              ),
            ),
          ),
          matchesSubset: (row) => portfolioIdSet.has(row.portfolioId),
        });
      },
      queryKey: withPortfolioCollectionQueryScope(
        queryKeys.portfolio.allocationHistoryCheckpoints,
        collectionQueryScope,
      ),
      startSync: true,
      staleTime: 20_000,
      syncMode: "on-demand",
    }),
  );
  allocationHistoryCheckpointsRef = allocationHistoryCheckpoints;
  allocationHistoryCheckpoints.createIndex((row) => row.portfolioId);

  return {
    allocationHistoryCheckpoints,
    assetPnl,
    assetPrices,
    assets,
    dashboardAssetSummaries,
    historyEvents,
    performancePoints,
    performanceSeries,
    performanceSummaries,
    performanceWindowStats,
    portfolioAssetUniverse,
    portfolioLivePositions,
    portfolioInstances,
    portfolioScheduledFunctions,
    portfolioSchedules,
    portfolioWorkflows,
    positions,
    vaults,
  };
}

export function buildPortfolioDetailViewQuery(input: {
  collections: PortfolioDomainCollections;
  historyLimit?: number;
  portfolioId: string;
}) {
  const normalizedPortfolioId = input.portfolioId.trim();
  const historyLimit = normalizePositiveInteger(input.historyLimit, 30);

  return (queryBuilder: InitialQueryBuilder) =>
    queryBuilder
      .from({ portfolioInstance: input.collections.portfolioInstances })
      .where(({ portfolioInstance }) =>
        eq(portfolioInstance.id, normalizedPortfolioId),
      )
      .select(({ portfolioInstance }) => ({
        assetPnl: toArray(
          queryBuilder
            .from({ assetPnl: input.collections.assetPnl })
            .where(({ assetPnl }) =>
              eq(assetPnl.portfolioId, portfolioInstance.id),
            )
            .select(({ assetPnl }) => assetPnl),
        ),
        historyEvents: toArray(
          queryBuilder
            .from({ historyEvent: input.collections.historyEvents })
            .where(({ historyEvent }) =>
              eq(historyEvent.portfolioId, portfolioInstance.id),
            )
            .orderBy(({ historyEvent }) => historyEvent.timestamp, "desc")
            .limit(historyLimit)
            .select(({ historyEvent }) => historyEvent),
        ),
        performancePoints: toArray(
          queryBuilder
            .from({ performancePoint: input.collections.performancePoints })
            .where(({ performancePoint }) =>
              eq(performancePoint.portfolioId, portfolioInstance.id),
            )
            .orderBy(
              ({ performancePoint }) => performancePoint.timestamp,
              "asc",
            )
            .select(({ performancePoint }) => performancePoint),
        ),
        performanceSeries: toArray(
          queryBuilder
            .from({
              performanceSeriesPoint: input.collections.performanceSeries,
            })
            .where(({ performanceSeriesPoint }) =>
              eq(performanceSeriesPoint.portfolioId, portfolioInstance.id),
            )
            .orderBy(
              ({ performanceSeriesPoint }) => performanceSeriesPoint.timestamp,
              "asc",
            )
            .select(({ performanceSeriesPoint }) => performanceSeriesPoint),
        ),
        performanceSummary: materialize(
          queryBuilder
            .from({
              performanceSummary: input.collections.performanceSummaries,
            })
            .where(({ performanceSummary }) =>
              eq(performanceSummary.portfolioId, portfolioInstance.id),
            )
            .orderBy(
              ({ performanceSummary }) => performanceSummary.updatedAt,
              "desc",
            )
            .limit(1)
            .select(({ performanceSummary }) => performanceSummary)
            .findOne(),
        ),
        performanceWindowStats: materialize(
          queryBuilder
            .from({
              performanceWindowStats: input.collections.performanceWindowStats,
            })
            .where(({ performanceWindowStats }) =>
              eq(performanceWindowStats.portfolioId, portfolioInstance.id),
            )
            .orderBy(
              ({ performanceWindowStats }) => performanceWindowStats.updatedAt,
              "desc",
            )
            .limit(1)
            .select(({ performanceWindowStats }) => performanceWindowStats)
            .findOne(),
        ),
        portfolioAssetRows: toArray(
          queryBuilder
            .from({ link: input.collections.portfolioAssetUniverse })
            .where(({ link }) => eq(link.portfolioId, portfolioInstance.id))
            .leftJoin({ asset: input.collections.assets }, ({ asset, link }) =>
              eq(link.assetId, asset.id),
            )
            .leftJoin(
              { assetPrice: input.collections.assetPrices },
              ({ assetPrice, link }) => eq(link.assetId, assetPrice.assetId),
            )
            .select(({ asset, assetPrice, link }) => ({
              asset,
              assetPrice,
              link,
            })),
        ),
        portfolioInstance,
        liveTokenRows: toArray(
          queryBuilder
            .from({ position: input.collections.portfolioLivePositions })
            .where(({ position }) =>
              eq(position.portfolioId, portfolioInstance.id),
            )
            .leftJoin(
              { asset: input.collections.assets },
              ({ asset, position }) => eq(position.assetId, asset.id),
            )
            .leftJoin(
              { assetPrice: input.collections.assetPrices },
              ({ assetPrice, position }) =>
                eq(position.assetId, assetPrice.assetId),
            )
            .select(({ asset, assetPrice, position }) => ({
              asset,
              assetPrice,
              position,
            })),
        ),
        portfolioSchedule: materialize(
          queryBuilder
            .from({ schedule: input.collections.portfolioSchedules })
            .where(({ schedule }) =>
              eq(schedule.portfolioId, portfolioInstance.id),
            )
            .orderBy(({ schedule }) => schedule.updatedAt, "desc")
            .limit(1)
            .select(({ schedule }) => schedule)
            .findOne(),
        ),
        tokenRows: toArray(
          queryBuilder
            .from({ position: input.collections.positions })
            .where(({ position }) =>
              eq(position.portfolioId, portfolioInstance.id),
            )
            .leftJoin(
              { asset: input.collections.assets },
              ({ asset, position }) => eq(position.assetId, asset.id),
            )
            .leftJoin(
              { assetPrice: input.collections.assetPrices },
              ({ assetPrice, position }) =>
                eq(position.assetId, assetPrice.assetId),
            )
            .select(({ asset, assetPrice, position }) => ({
              asset,
              assetPrice,
              position,
            })),
        ),
        vaults: toArray(
          queryBuilder
            .from({ vault: input.collections.vaults })
            .where(({ vault }) => eq(vault.portfolioId, portfolioInstance.id))
            .select(({ vault }) => vault),
        ),
      }))
      .findOne();
}

export type PortfolioDomainCollections = ReturnType<
  typeof createPortfolioDomainCollectionDefinitions
>;
